import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Form, Spinner } from "react-bootstrap";
import { getPtoTotals_Admin } from "../api/PtoDetailsApi";
import { useHistory } from "react-router-dom";
import Decimal from "../../common/typeFormatting/DecimalFmt";
import "./AdminStyles.css";

const AdminPtoDashboard = () => {
  // State management
  const [rawData, setRawData] = useState([]); // Original data from API
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [groupBy, setGroupBy] = useState("department"); // Default to department 
  const [sortBy, setSortBy] = useState("lastName"); // Default sort by lastName
  const [sortDirection, setSortDirection] = useState("asc"); // Default ascending
  const [processedData, setProcessedData] = useState([]); // Processed data after filtering/grouping/sorting
  
  const history = useHistory();

  // Fetch data from API once
  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const allEmployeesPtoData = await getPtoTotals_Admin();
        setRawData(allEmployeesPtoData);
      } catch (error) {
        console.error("Error fetching all employees PTO data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  // Process data whenever any parameters change
  useEffect(() => {
    if (rawData.length === 0) return;

    // Step 1: Filter data based on search text (by lastName only with startsWith)
    const filtered = rawData.filter(employee => {
      const searchText = filterText.toLowerCase();
      return employee.lastName.toLowerCase().startsWith(searchText);
    });

    // Step 2: Create groups based on groupBy
    const groups = {};
    
    if (groupBy === "none") {
      // If no grouping, put all in one group
      groups["All Employees"] = {
        title: "All Employees",
        employees: [...filtered]
      };
    } else if (groupBy === "manager") {
      // Group by manager
      filtered.forEach(employee => {
        // Use manager's name as the key
        const managerName = `${employee.immediate_Manager_FirstName || ''} ${employee.immediate_Manager_LastName || ''}`.trim();
        const groupKey = managerName || "No Manager";
        
        if (!groups[groupKey]) {
          groups[groupKey] = {
            title: managerName || "No Manager",
            employees: []
          };
        }
        
        groups[groupKey].employees.push(employee);
      });
    } else if (groupBy === "department") {
      // Group by department
      filtered.forEach(employee => {
        const department = employee.department || "No Department";
        
        if (!groups[department]) {
          groups[department] = {
            title: department,
            employees: []
          };
        }
        
        groups[department].employees.push(employee);
      });
    } else if (groupBy === "status") {
      // Group by status
      filtered.forEach(employee => {
        const status = employee.employeeStatus || "Unknown Status";
        
        if (!groups[status]) {
          groups[status] = {
            title: status,
            employees: []
          };
        }
        
        groups[status].employees.push(employee);
      });
    }
    
    // Step 3: Calculate PTO totals and sort within each group
    Object.keys(groups).forEach(key => {
      // Calculate the total PTO for this group
      let totalPto = 0;
      groups[key].employees.forEach(employee => {
        if (typeof employee.ptoBalance === 'number') {
          totalPto += employee.ptoBalance;
        }
      });
      
      // Add the total to the group
      groups[key].totalPto = totalPto;
      
      // Sort employees in this group
      groups[key].employees.sort((a, b) => {
        if (sortBy === "lastName") {
          const compareResult = a.lastName.localeCompare(b.lastName);
          return sortDirection === "asc" ? compareResult : -compareResult;
        } else if (sortBy === "ptoBalance") {
          // Handle possible null/undefined values
          const aValue = typeof a.ptoBalance === 'number' ? a.ptoBalance : 0;
          const bValue = typeof b.ptoBalance === 'number' ? b.ptoBalance : 0;
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }
        return 0;
      });
    });
    
    // Step 4: Create array of group objects with specific order
    const orderedGroups = [];
    
    if (groupBy === "none") {
      // Just one group for no grouping
      orderedGroups.push(groups["All Employees"]);
    } else if (groupBy === "manager") {
      // Sort managers alphabetically
      const managerKeys = Object.keys(groups).sort();
      managerKeys.forEach(key => {
        orderedGroups.push(groups[key]);
      });
    } else if (groupBy === "status") {
      // Custom order for status groups: Active, Inactive, Terminated, then others alphabetically
      const priorityOrder = ["Active", "Inactive", "Terminated"];
      
      // First add the priority groups in specified order
      priorityOrder.forEach(status => {
        if (groups[status]) {
          orderedGroups.push(groups[status]);
        }
      });
      
      // Then add all other groups alphabetically
      const otherStatuses = Object.keys(groups)
        .filter(status => !priorityOrder.includes(status))
        .sort();
      
      otherStatuses.forEach(status => {
        orderedGroups.push(groups[status]);
      });
    } else {
      // Alphabetical order for departments
      const departmentKeys = Object.keys(groups).sort();
      departmentKeys.forEach(key => {
        orderedGroups.push(groups[key]);
      });
    }
    
    // Step 5: Update processed data state
    setProcessedData(orderedGroups);
    
  }, [rawData, filterText, groupBy, sortBy, sortDirection]);

  // Event handlers
  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  const handleGroupChange = (e) => {
    setGroupBy(e.target.value);
  };
  
  const handleColumnHeaderClick = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column as sort field and reset to ascending
      setSortBy(column);
      setSortDirection("asc");
    }
  };
  
  const handleRowClick = (employeeId) => {
    history.push(`/pto/admin/employee/${employeeId}`);
  };

  // CSS for sortable headers
  const sortableHeaderStyle = {
    cursor: 'pointer',
    userSelect: 'none',
  };

  return (
    <Container fluid>
      <Row className="mt-3 mb-3 align-items-center">
        <Col md={6}>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="Search by last name..."
              value={filterText}
              onChange={handleFilterChange}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Control as="select" value={groupBy} onChange={handleGroupChange}>
              <option value="none">No Grouping</option>
              <option value="department">Group by Department</option>
              <option value="manager">Group by Manager</option>
              <option value="status">Group by Status</option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col>
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
              <p className="mt-3">Loading employee PTO data...</p>
            </div>
          ) : (
            <>
              {processedData.length > 0 ? (
                processedData.map((group, index) => (
                  <Card className="mb-4" key={index}>
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{group.title}</strong> ({group.employees.length} {group.employees.length === 1 ? 'employee' : 'employees'})
                        </div>
                        <div>
                          <strong>Total PTO: </strong>
                          <Decimal>{group.totalPto}</Decimal> hours
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Table hover responsive>
                        <thead>
                          <tr>
                            <th 
                              onClick={() => handleColumnHeaderClick("lastName")}
                              style={{
                                ...sortableHeaderStyle,
                                ...(sortBy === "lastName" ? {fontWeight: 'bold', fontStyle: 'italic'} : {})
                              }}
                            >
                              Last Name {sortBy === "lastName" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th>First Name</th>
                            <th>Employee #</th>
                            <th>Department</th>
                            <th>Status</th>
                            <th 
                              onClick={() => handleColumnHeaderClick("ptoBalance")}
                              style={{
                                ...sortableHeaderStyle, 
                                textAlign: 'right',
                                ...(sortBy === "ptoBalance" ? {fontWeight: 'bold', fontStyle: 'italic'} : {})
                              }}
                            >
                              PTO Balance (Hours) {sortBy === "ptoBalance" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.employees.map((employee) => (
                            <tr 
                              key={employee.employeeId}
                              onClick={() => handleRowClick(employee.employeeId)}
                              style={{ cursor: 'pointer' }}
                              className="hover-highlight"
                            >
                              <td>{employee.lastName}</td>
                              <td>{employee.firstName}</td>
                              <td>{employee.employeeNumber}</td>
                              <td>{employee.department || "-"}</td>
                              <td>{employee.employeeStatus || "-"}</td>
                              <td className="text-right">
                                {typeof employee.ptoBalance === 'number' ? 
                                  <Decimal>{employee.ptoBalance}</Decimal> : 
                                  employee.ptoBalance}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <Card>
                  <Card.Body className="text-center">
                    <p>No matching employees found</p>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default AdminPtoDashboard;